import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Package, MapPin, ArrowRight, Loader2, User } from "lucide-react";

const TAB_MAP = {
  Active: ["Accepted", "In Transit", "Matched", "Pickup Pending", "Picked Up", "Out for Delivery"],
  Upcoming: ["Requested"],
  Completed: ["Delivered"],
  Cancelled: ["Cancelled"],
};

export default function MyDeliveries() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/deliveries/mine"); setItems(data.deliveries); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div>
      <PageHeader title="My Deliveries" subtitle="Track every package you've sent." action={<Button onClick={() => navigate("/create-delivery")} className="rounded-full" data-testid="mydeliveries-create">New Delivery</Button>} />
      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-primary animate-spin" /></div> : (
        <Tabs defaultValue="Active">
          <TabsList className="rounded-xl mb-5 flex-wrap h-auto">{Object.keys(TAB_MAP).map((t) => <TabsTrigger key={t} value={t} data-testid={`mydeliveries-tab-${t.toLowerCase()}`} className="rounded-lg">{t}</TabsTrigger>)}</TabsList>
          {Object.entries(TAB_MAP).map(([tab, statuses]) => {
            const list = items.filter((d) => statuses.includes(d.status));
            return (
              <TabsContent key={tab} value={tab}>
                {list.length === 0 ? (
                  <EmptyState icon={Package} title={`No ${tab.toLowerCase()} deliveries.`} description={tab === "Upcoming" ? "Create a delivery request to get started." : "Nothing here yet."} actionLabel={tab === "Upcoming" ? "Create a delivery" : undefined} actionTo="/create-delivery" testid={`empty-deliveries-${tab.toLowerCase()}`} />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {list.map((d) => (
                      <button key={d.id} onClick={() => navigate(`/deliveries/${d.id}`)} data-testid={`mydelivery-card-${d.id}`} className="text-left rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-xs text-slate-400">{d.id}</span>
                          <StatusBadge status={d.status} />
                        </div>
                        <p className="mt-3 text-sm font-medium text-slate-800 line-clamp-1">{d.description}</p>
                        <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                          <MapPin className="w-3.5 h-3.5 text-primary" /><span className="truncate">{d.pickup}</span>
                          <ArrowRight className="w-3 h-3" /><span className="truncate">{d.destination}</span>
                        </div>
                        <div className="mt-2 text-xs text-slate-400">{d.delivery_date}</div>
                        {d.traveller && <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500"><User className="w-3.5 h-3.5" /> {d.traveller.name}</div>}
                      </button>
                    ))}
                  </div>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      )}
    </div>
  );
}
